import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Shield, ArrowLeft, RefreshCw, Square, CheckCircle, XCircle,
  Loader2, Clock, Activity, Bug, Key, Users, Database, Crown,
  Lock, Network, Eye, AlertTriangle, Terminal, BookOpen,
  ChevronDown, ChevronUp, Star, TrendingUp, TrendingDown,
  FileText, Download, Zap, Target, Crosshair, Circle,
  Info, ExternalLink, Copy, Check, Brain, Sword, Camera,
  Lightbulb, ShieldAlert, ArrowRight, Code2, Sparkles
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
});

// ── Helpers ──────────────────────────────────────────────────────────────────

const statusConfig = {
  running:     { color: 'bg-blue-500/20 text-blue-400 border-blue-500/40', icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />, label: 'En cours' },
  completed:   { color: 'bg-green-500/20 text-green-400 border-green-500/40', icon: <CheckCircle className="w-3.5 h-3.5" />, label: 'Terminé' },
  failed:      { color: 'bg-red-500/20 text-red-400 border-red-500/40', icon: <XCircle className="w-3.5 h-3.5" />, label: 'Échoué' },
  stopped:     { color: 'bg-gray-500/20 text-gray-400 border-gray-500/40', icon: <Square className="w-3.5 h-3.5" />, label: 'Arrêté' },
  initialized: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40', icon: <Clock className="w-3.5 h-3.5" />, label: 'Initialisé' },
};

const StatusBadge = ({ status, large = false }) => {
  const cfg = statusConfig[status] || statusConfig.initialized;
  return (
    <span className={`inline-flex items-center gap-1.5 px-${large ? 3 : 2} py-${large ? 1 : 0.5} rounded-full border font-medium ${large ? 'text-sm' : 'text-xs'} ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

const sevColor = (sev) => ({
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20 border-red-500/40 text-red-400', dot: 'bg-red-500' },
  high:     { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/20 border-orange-500/40 text-orange-400', dot: 'bg-orange-500' },
  medium:   { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400', dot: 'bg-yellow-500' },
  low:      { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/20 border-blue-500/40 text-blue-400', dot: 'bg-blue-400' },
  info:     { bg: 'bg-gray-500/10', border: 'border-gray-700', text: 'text-gray-400', badge: 'bg-gray-500/20 border-gray-600 text-gray-400', dot: 'bg-gray-500' },
}[(sev || '').toLowerCase()] || { bg: 'bg-gray-500/10', border: 'border-gray-700', text: 'text-gray-400', badge: 'bg-gray-500/20 border-gray-600 text-gray-400', dot: 'bg-gray-500' });

const phaseIcon = (phase) => {
  const map = {
    reconnaissance:       <Eye className="w-4 h-4 text-blue-400" />,
    enumeration:          <Database className="w-4 h-4 text-purple-400" />,
    credential_harvesting:<Key className="w-4 h-4 text-yellow-400" />,
    lateral_movement:     <Network className="w-4 h-4 text-orange-400" />,
    privilege_escalation: <Crown className="w-4 h-4 text-red-400" />,
    persistence:          <Lock className="w-4 h-4 text-gray-400" />,
  };
  return map[phase] || <Activity className="w-4 h-4 text-gray-400" />;
};

const formatDuration = (secs) => {
  if (!secs) return '—';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};

const KpiCard = ({ icon, label, value, sub, color = 'brand-green', danger }) => (
  <div className={`bg-brand-black-light rounded-xl border ${danger ? 'border-red-500/30' : 'border-gray-800'} p-4`}>
    <div className="flex items-start justify-between">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${danger ? 'bg-red-500/20' : `bg-${color}/10`}`}>
        {icon}
      </div>
    </div>
    <div className={`text-3xl font-black mt-2 ${danger ? 'text-red-400' : `text-${color}`}`}>{value}</div>
    <div className="text-sm font-medium text-white mt-0.5">{label}</div>
    {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="p-1 text-gray-600 hover:text-gray-300 rounded transition-colors">
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

// ── Risk Gauge ────────────────────────────────────────────────────────────────

const RiskGauge = ({ findings }) => {
  const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
  (findings || []).forEach(f => {
    const s = (f.severity || 'info').toLowerCase();
    if (counts[s] !== undefined) counts[s]++;
  });
  const score = counts.critical * 25 + counts.high * 10 + counts.medium * 4 + counts.low * 1;
  const capped = Math.min(100, score);
  const riskLabel = capped >= 50 ? 'CRITIQUE' : capped >= 25 ? 'ÉLEVÉ' : capped >= 10 ? 'MOYEN' : 'FAIBLE';
  const riskColor = capped >= 50 ? 'text-red-400' : capped >= 25 ? 'text-orange-400' : capped >= 10 ? 'text-yellow-400' : 'text-green-400';

  return (
    <div className="bg-brand-black-light rounded-xl border border-gray-800 p-5">
      <div className="text-sm font-semibold text-gray-400 mb-4">Niveau de risque global</div>
      <div className="flex items-center gap-6">
        <div className="relative w-24 h-24">
          <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#1f2937" strokeWidth="12" />
            <circle
              cx="50" cy="50" r="40" fill="none"
              stroke="url(#rg)" strokeWidth="12"
              strokeDasharray={`${2 * Math.PI * 40 * capped / 100} ${2 * Math.PI * 40 * (1 - capped / 100)}`}
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="rg" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={capped >= 50 ? '#dc2626' : capped >= 25 ? '#ea580c' : capped >= 10 ? '#ca8a04' : '#16a34a'} />
                <stop offset="100%" stopColor={capped >= 50 ? '#f87171' : capped >= 25 ? '#fb923c' : capped >= 10 ? '#facc15' : '#4ade80'} />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center flex-col">
            <span className={`text-xl font-black ${riskColor}`}>{capped}</span>
            <span className="text-xs text-gray-500">/100</span>
          </div>
        </div>
        <div className="flex-1">
          <div className={`text-2xl font-black mb-2 ${riskColor}`}>{riskLabel}</div>
          <div className="space-y-1.5">
            {[
              { key: 'critical', label: 'Critique', color: 'bg-red-500' },
              { key: 'high', label: 'Élevé', color: 'bg-orange-500' },
              { key: 'medium', label: 'Moyen', color: 'bg-yellow-500' },
              { key: 'low', label: 'Faible', color: 'bg-blue-400' },
            ].map(({ key, label, color }) => (
              <div key={key} className="flex items-center gap-2 text-xs">
                <div className={`w-2 h-2 rounded-full ${color}`} />
                <span className="text-gray-400 w-14">{label}</span>
                <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div className={`h-full ${color} rounded-full`} style={{ width: `${Math.min(100, counts[key] * 20)}%` }} />
                </div>
                <span className="text-gray-300 w-4 text-right font-bold">{counts[key]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Phase Summary ─────────────────────────────────────────────────────────────

const PhaseSummary = ({ steps }) => {
  const phases = {};
  (steps || []).forEach(s => {
    if (!phases[s.phase]) phases[s.phase] = { total: 0, done: 0, failed: 0, findings: 0 };
    phases[s.phase].total++;
    if (s.status === 'completed') phases[s.phase].done++;
    if (s.status === 'failed') phases[s.phase].failed++;
    phases[s.phase].findings += (s.findings || []).length;
  });

  return (
    <div className="bg-brand-black-light rounded-xl border border-gray-800 p-5">
      <div className="text-sm font-semibold text-gray-400 mb-4">Phases d'attaque</div>
      <div className="space-y-3">
        {Object.entries(phases).map(([phase, stats]) => {
          const pct = stats.total ? Math.round(stats.done / stats.total * 100) : 0;
          return (
            <div key={phase}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  {phaseIcon(phase)}
                  <span className="text-xs font-medium text-gray-300 capitalize">{phase.replace(/_/g, ' ')}</span>
                  {stats.findings > 0 && (
                    <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/30">
                      {stats.findings} finding{stats.findings > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">{stats.done}/{stats.total}</span>
              </div>
              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${stats.failed > 0 ? 'bg-gradient-to-r from-red-600 to-orange-500' : 'bg-gradient-to-r from-brand-green to-green-400'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Step Card ─────────────────────────────────────────────────────────────────

const StepCard = ({ step, index }) => {
  const [expanded, setExpanded] = useState(false);

  const borderColor =
    step.status === 'completed' ? 'border-green-500/30' :
    step.status === 'failed'    ? 'border-red-500/30' :
    step.status === 'running'   ? 'border-blue-500/30 animate-pulse' :
    step.status === 'skipped'   ? 'border-gray-700' : 'border-gray-800';

  const statusIcon =
    step.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" /> :
    step.status === 'failed'    ? <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" /> :
    step.status === 'running'   ? <Loader2 className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" /> :
    step.status === 'skipped'   ? <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" /> :
                                  <Circle className="w-4 h-4 text-gray-600 flex-shrink-0" />;

  return (
    <div className={`rounded-xl border overflow-hidden bg-brand-black-light transition-all ${borderColor}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-xs font-mono text-gray-600 w-5 flex-shrink-0">#{index + 1}</span>
        {statusIcon}
        {phaseIcon(step.phase)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{step.name}</span>
            {step.mitre_id && (
              <span className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded border border-purple-500/30 font-mono">
                {step.mitre_id}
              </span>
            )}
            {step.tool && (
              <span className="text-xs px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded border border-gray-700">
                {step.tool}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-500 capitalize">{step.phase?.replace(/_/g, ' ')}</span>
            {step.duration_seconds && (
              <span className="text-xs text-gray-600">{formatDuration(step.duration_seconds)}</span>
            )}
          </div>
        </div>
        {(step.findings || []).length > 0 && (
          <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/30 flex-shrink-0">
            {step.findings.length} finding{step.findings.length > 1 ? 's' : ''}
          </span>
        )}
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-3 border-t border-gray-800/60 space-y-3">
          {step.description && (
            <p className="text-sm text-gray-400">{step.description}</p>
          )}

          {step.command && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 font-semibold">Commande</span>
                <CopyBtn text={step.command} />
              </div>
              <div className="bg-black/50 rounded-lg p-3 font-mono text-xs text-green-400 border border-gray-800 overflow-x-auto">
                {step.command}
              </div>
            </div>
          )}

          {step.output && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 font-semibold">Output</span>
                <CopyBtn text={step.output} />
              </div>
              <div className="bg-black/50 rounded-lg p-3 font-mono text-xs text-gray-300 max-h-48 overflow-y-auto border border-gray-800 whitespace-pre-wrap leading-relaxed">
                {step.output}
              </div>
            </div>
          )}

          {step.error && (
            <div>
              <span className="text-xs text-red-500 font-semibold">Erreur</span>
              <div className="mt-1 bg-red-500/5 rounded-lg p-3 font-mono text-xs text-red-300 border border-red-500/20 whitespace-pre-wrap">
                {step.error}
              </div>
            </div>
          )}

          {(step.ai_analysis || step.ai_explanation) && (
            <div className="bg-brand-green/5 border border-brand-green/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Zap className="w-3.5 h-3.5 text-brand-green" />
                <span className="text-xs text-brand-green font-semibold">Analyse IA</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{step.ai_analysis || step.ai_explanation}</p>
            </div>
          )}

          {(step.findings || []).length > 0 && (
            <div>
              <span className="text-xs text-gray-500 font-semibold mb-2 block">Findings de cette étape</span>
              <div className="space-y-1.5">
                {step.findings.map((f, fi) => {
                  const title = typeof f === 'string' ? f : (f.title || JSON.stringify(f));
                  const sev = (f?.severity || 'info').toLowerCase();
                  const sc = sevColor(sev);
                  return (
                    <div key={fi} className={`flex items-start gap-2 p-2.5 rounded-lg border ${sc.bg} ${sc.border}`}>
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sc.dot}`} />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-white">{title}</span>
                        {f?.description && <p className="text-xs text-gray-400 mt-0.5">{f.description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Finding Card ──────────────────────────────────────────────────────────────

const FindingCard = ({ finding, index }) => {
  const [expanded, setExpanded] = useState(false);
  const sev = (finding.severity || 'info').toLowerCase();
  const sc = sevColor(sev);
  const title = finding.title || finding.finding || (typeof finding === 'string' ? finding : 'Finding');

  return (
    <div className={`rounded-xl border overflow-hidden ${sc.bg} ${sc.border}`}>
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${sc.bg} border ${sc.border}`}>
          <span className="text-xs font-black">{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-bold uppercase ${sc.badge}`}>
              {sev}
            </span>
            {finding.finding_type && (
              <span className="text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700">
                {finding.finding_type}
              </span>
            )}
          </div>
          {finding.description && !expanded && (
            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{finding.description}</p>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1" />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-3 border-t border-gray-800/40 space-y-3">
          {finding.description && (
            <div>
              <span className="text-xs font-semibold text-gray-500">Description</span>
              <p className="mt-1 text-sm text-gray-300 leading-relaxed">{finding.description}</p>
            </div>
          )}

          {finding.evidence && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-500">Capture / Preuve</span>
                </div>
                <CopyBtn text={finding.evidence} />
              </div>
              {/* Terminal-style evidence display */}
              <div className="rounded-lg overflow-hidden border border-gray-700">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-900 border-b border-gray-700">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-xs text-gray-500 font-mono flex-1 text-center">evidence — {title}</span>
                </div>
                <div className="bg-black/80 p-3 font-mono text-xs text-gray-300 max-h-48 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                  <span className="text-green-400">$ </span>{finding.evidence}
                </div>
              </div>
            </div>
          )}

          {finding.recommendation && (
            <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                <span className="text-xs text-green-400 font-semibold">Remédiation recommandée</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{finding.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── AI Section Renderer ───────────────────────────────────────────────────────

const AISectionBlock = ({ title, icon, content, color = 'brand-green', collapsible = false }) => {
  const [open, setOpen] = useState(true);

  // Convert markdown-ish content to structured display
  const lines = (content || '').split('\n').filter(l => l.trim());

  return (
    <div className={`rounded-xl border overflow-hidden bg-brand-black-light border-${color}/20`}>
      <div
        className={`flex items-center justify-between px-5 py-3.5 ${collapsible ? 'cursor-pointer hover:bg-white/[0.02]' : ''} border-b border-${color}/10`}
        onClick={collapsible ? () => setOpen(!open) : undefined}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg bg-${color}/10 flex items-center justify-center flex-shrink-0`}>
            {icon}
          </div>
          <span className={`font-bold text-white text-sm`}>{title}</span>
        </div>
        {collapsible && (open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />)}
      </div>
      {open && (
        <div className="px-5 py-4 space-y-2">
          {lines.map((line, i) => {
            const trimmed = line.trim();
            // Numbered list
            if (/^\d+\.\s/.test(trimmed)) {
              return (
                <div key={i} className="flex items-start gap-2.5">
                  <span className={`text-xs font-black text-${color} w-5 flex-shrink-0 mt-0.5`}>{trimmed.match(/^\d+/)[0]}.</span>
                  <p className="text-sm text-gray-300 leading-relaxed">{trimmed.replace(/^\d+\.\s/, '')}</p>
                </div>
              );
            }
            // Bullet
            if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
              return (
                <div key={i} className="flex items-start gap-2.5">
                  <ArrowRight className={`w-3.5 h-3.5 text-${color} flex-shrink-0 mt-0.5`} />
                  <p className="text-sm text-gray-300 leading-relaxed">{trimmed.replace(/^[-•]\s/, '')}</p>
                </div>
              );
            }
            // Code block (4 spaces or backtick)
            if (trimmed.startsWith('`') || line.startsWith('    ')) {
              return (
                <div key={i} className="rounded-lg bg-black/60 border border-gray-800 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900/60 border-b border-gray-800">
                    <Terminal className="w-3 h-3 text-gray-500" />
                    <span className="text-xs text-gray-500">commande</span>
                  </div>
                  <div className="px-3 py-2 font-mono text-xs text-green-400 whitespace-pre-wrap">
                    {trimmed.replace(/^`+|`+$/g, '')}
                  </div>
                </div>
              );
            }
            // Bold header (### or **)
            if (trimmed.startsWith('###') || trimmed.startsWith('**')) {
              return (
                <p key={i} className={`text-sm font-bold text-${color} mt-3 mb-1`}>
                  {trimmed.replace(/^#+\s*|^\*\*|\*\*$/g, '')}
                </p>
              );
            }
            // Subheader (##)
            if (trimmed.startsWith('##')) {
              return (
                <p key={i} className="text-sm font-bold text-white mt-2">
                  {trimmed.replace(/^#+\s*/, '')}
                </p>
              );
            }
            // Normal paragraph
            if (trimmed) {
              return <p key={i} className="text-sm text-gray-300 leading-relaxed">{trimmed}</p>;
            }
            return <div key={i} className="h-2" />;
          })}
          {lines.length === 0 && (
            <p className="text-sm text-gray-500 italic">Aucune information disponible.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Evidence Capture Panel ────────────────────────────────────────────────────

const EvidenceCaptures = ({ findings }) => {
  const withEvidence = findings.filter(f => f.evidence && f.evidence.trim());

  const downloadAll = () => {
    const lines = withEvidence.map((f, i) => {
      const sev = (f.severity || 'info').toUpperCase();
      return `${'='.repeat(70)}\n[${i + 1}] [${sev}] ${f.title || 'Finding'}\n${'='.repeat(70)}\n${f.evidence}\n`;
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pentest_ad_preuves.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (withEvidence.length === 0) {
    return (
      <div className="text-center py-12">
        <Camera className="w-10 h-10 text-gray-700 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Aucune capture de preuve disponible</p>
        <p className="text-gray-600 text-xs mt-1">Les findings de cette session ne contiennent pas de preuves textuelles.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-400">
          <Camera className="w-4 h-4" />
          <span className="text-sm font-medium">{withEvidence.length} preuve{withEvidence.length > 1 ? 's' : ''} disponible{withEvidence.length > 1 ? 's' : ''}</span>
        </div>
        <button
          onClick={downloadAll}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-xs hover:bg-gray-700 border border-gray-700 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Télécharger toutes les preuves (.txt)
        </button>
      </div>
      <div className="space-y-4">
        {withEvidence.map((f, i) => {
          const sev = (f.severity || 'info').toLowerCase();
          const sc = sevColor(sev);
          return (
            <div key={i} className={`rounded-xl border overflow-hidden ${sc.border}`}>
              {/* Header */}
              <div className={`flex items-center justify-between px-4 py-2.5 ${sc.bg} border-b ${sc.border}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${sc.dot}`} />
                  <span className="text-sm font-bold text-white">{f.title || 'Finding'}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded border font-bold uppercase ${sc.badge}`}>{sev}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500">Preuve #{i + 1}</span>
                  <CopyBtn text={f.evidence} />
                </div>
              </div>
              {/* Terminal window */}
              <div className="bg-black/90">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] border-b border-gray-800">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                  </div>
                  <span className="text-xs text-gray-500 font-mono">pentest@ad-session ~ evidence</span>
                </div>
                <div className="p-4 font-mono text-xs text-gray-300 max-h-64 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                  <span className="text-green-400">$</span> <span className="text-blue-300"># Evidence for: {f.title}</span>
                  {'\n'}{f.evidence}
                </div>
              </div>
              {/* Footer with description if exists */}
              {f.description && (
                <div className="px-4 py-2.5 bg-brand-black-light border-t border-gray-800">
                  <p className="text-xs text-gray-400">{f.description}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const ADSessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [polling, setPolling] = useState(false);
  const [stepFilter, setStepFilter] = useState('all');
  const [findingFilter, setFindingFilter] = useState('all');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSubTab, setAiSubTab] = useState('analysis');
  const aiTriggeredRef = useRef(false);

  const fetchSession = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/ad-wizard/sessions/${sessionId}/status`, {
        headers: getAuthHeaders(),
      });
      if (!r.ok) {
        setError(`Session introuvable (HTTP ${r.status})`);
        return;
      }
      const d = await r.json();
      setData(d);
      setPolling(d.is_running || false);
    } catch (e) {
      setError(`Erreur réseau : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(fetchSession, 4000);
    return () => clearInterval(t);
  }, [polling, fetchSession]);

  // Charger l'analyse depuis la DB si déjà générée, sinon auto-déclencher
  useEffect(() => {
    if (!data) return;
    // Si une analyse est déjà sauvegardée en DB, la charger directement
    if (data.ai_analysis && !aiAnalysis) {
      setAiAnalysis(data.ai_analysis);
      aiTriggeredRef.current = true;
      return;
    }
    // Sinon, déclencher automatiquement quand la session est terminée
    if (
      data.status === 'completed' &&
      !aiAnalysis &&
      !loadingAI &&
      !aiTriggeredRef.current
    ) {
      aiTriggeredRef.current = true;
      generateAIAnalysis();
    }
  }, [data?.status, data?.ai_analysis]); // eslint-disable-line

  const handleStop = async () => {
    await fetch(`${API_BASE}/api/v1/ad-wizard/sessions/${sessionId}/stop`, {
      method: 'POST', headers: getAuthHeaders(),
    });
    setPolling(false);
    fetchSession();
  };

  const generateAIAnalysis = async () => {
    setLoadingAI(true);
    setAiError(null);
    try {
      const r = await fetch(`${API_BASE}/api/v1/ad-wizard/sessions/${sessionId}/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = await r.json();
      setAiAnalysis(result);
    } catch (e) {
      setAiError(`Erreur lors de la génération : ${e.message}`);
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
          <span>Chargement de la session AD…</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-white font-semibold mb-1">Session introuvable</p>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <button onClick={() => navigate('/ad')} className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg text-sm hover:bg-brand-green/30 transition-colors">
            Retour à la page AD
          </button>
        </div>
      </div>
    );
  }

  // Compute derived data
  const steps = data.steps || [];
  const timeline = data.timeline || [];
  const allFindings = (() => {
    const stepFindings = steps.filter(s => (s.findings || []).length > 0).flatMap(s =>
      s.findings.map(f => ({ ...f, _step: s }))
    );
    const sessionFindings = data.findings || [];
    return stepFindings.length > 0 ? stepFindings : sessionFindings;
  })();

  const sortedFindings = [...allFindings].sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    return (order[(a.severity || 'info').toLowerCase()] ?? 4) - (order[(b.severity || 'info').toLowerCase()] ?? 4);
  });

  const sevCounts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
  sortedFindings.forEach(f => {
    const s = (f.severity || 'info').toLowerCase();
    if (sevCounts[s] !== undefined) sevCounts[s]++;
  });

  const filteredSteps = stepFilter === 'all' ? steps : steps.filter(s => s.status === stepFilter);
  const filteredFindings = findingFilter === 'all' ? sortedFindings : sortedFindings.filter(f => (f.severity || 'info').toLowerCase() === findingFilter);

  const [done, total] = (data.progress || '0/0').split('/').map(Number);
  const pct = total ? Math.round(done / total * 100) : 0;
  const sessionType = steps.some(s => s.tool?.includes('ocd') || s.mitre_id?.includes('OCD')) ? 'OCD Mindmap 2025.03' : 'AD Attack Wizard';

  const tabs = [
    { id: 'overview', label: 'Synthèse', icon: <Activity className="w-4 h-4" /> },
    { id: 'steps', label: `Étapes (${steps.length})`, icon: <Terminal className="w-4 h-4" /> },
    { id: 'findings', label: `Findings${sortedFindings.length > 0 ? ` (${sortedFindings.length})` : ''}`, icon: <Bug className="w-4 h-4" />, badge: sevCounts.critical > 0 ? sevCounts.critical : null },
    { id: 'timeline', label: 'Timeline', icon: <Clock className="w-4 h-4" /> },
    { id: 'ai', label: 'Analyse IA', icon: <Brain className="w-4 h-4" />, highlight: true },
  ];

  // AI section config
  const aiSections = aiAnalysis?.sections || {};
  const sectionDefs = [
    {
      key: 'SYNTHÈSE DES DÉCOUVERTES',
      label: 'Synthèse des découvertes',
      icon: <Lightbulb className="w-4 h-4 text-yellow-400" />,
      color: 'yellow-400',
    },
    {
      key: 'CHEMINS D\'ATTAQUE IDENTIFIÉS',
      label: 'Chemins d\'attaque identifiés',
      icon: <Crosshair className="w-4 h-4 text-orange-400" />,
      color: 'orange-400',
    },
    {
      key: 'GUIDE DE COMPROMISSION AD',
      label: 'Guide de compromission Active Directory',
      icon: <Sword className="w-4 h-4 text-red-400" />,
      color: 'red-400',
    },
    {
      key: 'RECOMMANDATIONS PRIORITAIRES',
      label: 'Recommandations prioritaires',
      icon: <ShieldAlert className="w-4 h-4 text-brand-green" />,
      color: 'brand-green',
    },
  ];

  return (
    <div className="min-h-screen bg-brand-black pb-16">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-brand-black/95 backdrop-blur border-b border-gray-800 px-6 py-3">
        <div className="w-[82%] mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ad')}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="w-px h-5 bg-gray-700" />
            <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
              <Shield className="w-4 h-4 text-brand-green" />
            </div>
            <div>
              <span className="text-white font-bold">{data.target_domain || '—'}</span>
              <span className="text-gray-500 text-sm ml-2 font-mono">{data.dc_ip}</span>
              <span className="text-xs text-gray-600 ml-2">· Session {sessionId.slice(0, 8)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={data.status} />
            {data.is_running && (
              <button
                onClick={handleStop}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 border border-red-500/30 transition-colors"
              >
                <Square className="w-3.5 h-3.5" /> Stopper
              </button>
            )}
            <button
              onClick={fetchSession}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-4 h-4 ${polling ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="w-[82%] mx-auto pt-6 space-y-6">
        {/* Progress bar */}
        {data.is_running && (
          <div className="bg-brand-black-light rounded-xl border border-blue-500/30 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Session en cours — {data.current_phase || 'Analyse AD'}</span>
              </div>
              <span className="text-sm font-bold text-blue-400">{pct}%</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-1.5">{done}/{total} étapes</div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={<Bug className="w-5 h-5 text-orange-400" />}
            label="Findings détectés"
            value={sortedFindings.length}
            sub={`${sevCounts.critical} critique${sevCounts.critical > 1 ? 's' : ''}, ${sevCounts.high} élevé${sevCounts.high > 1 ? 's' : ''}`}
            color="orange-400"
            danger={sevCounts.critical > 0}
          />
          <KpiCard
            icon={<Activity className="w-5 h-5 text-brand-green" />}
            label="Étapes exécutées"
            value={`${done}/${total}`}
            sub={`${data.failed_steps || 0} échouée${(data.failed_steps || 0) > 1 ? 's' : ''}, ${data.skipped_steps || 0} ignorée${(data.skipped_steps || 0) > 1 ? 's' : ''}`}
            color="brand-green"
          />
          <KpiCard
            icon={<Key className="w-5 h-5 text-yellow-400" />}
            label="Credentials trouvés"
            value={data.credentials_found || 0}
            sub="Comptes compromis"
            color="yellow-400"
            danger={(data.credentials_found || 0) > 0}
          />
          <KpiCard
            icon={<Users className="w-5 h-5 text-purple-400" />}
            label="Utilisateurs énumérés"
            value={data.users_enumerated || 0}
            sub={`Durée : ${formatDuration(data.duration_seconds)}`}
            color="purple-400"
          />
        </div>

        {/* Alert banners */}
        {data.domain_compromised && (
          <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-sm font-bold text-red-400">Domaine compromis</span>
              <p className="text-xs text-gray-300 mt-0.5">
                Les techniques exécutées ont permis de démontrer la compromission du domaine Active Directory <strong>{data.target_domain}</strong>.
                Une action immédiate est requise.
              </p>
            </div>
          </div>
        )}
        {data.attack_paths_found && !data.domain_compromised && (
          <div className="flex items-start gap-3 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
            <TrendingUp className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-sm font-bold text-orange-400">Chemins d'attaque identifiés</span>
              <p className="text-xs text-gray-300 mt-0.5">Des vecteurs d'escalade de privilèges ou de mouvement latéral ont été identifiés dans le domaine.</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-800">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? tab.highlight ? 'border-purple-500 text-purple-400' : 'border-brand-green text-brand-green'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              {tab.icon} {tab.label}
              {tab.badge && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab: Overview ── */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RiskGauge findings={sortedFindings} />
            <PhaseSummary steps={steps} />

            {/* Session info */}
            <div className="bg-brand-black-light rounded-xl border border-gray-800 p-5 lg:col-span-2">
              <div className="text-sm font-semibold text-gray-400 mb-4">Informations de session</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Domaine cible', value: data.target_domain || '—' },
                  { label: 'DC IP', value: data.dc_ip || '—', mono: true },
                  { label: 'Type de pentest', value: sessionType },
                  { label: 'Session ID', value: sessionId.slice(0, 16) + '…', mono: true },
                  { label: 'Démarré', value: data.started_at ? new Date(data.started_at).toLocaleString() : '—' },
                  { label: 'Terminé', value: data.completed_at ? new Date(data.completed_at).toLocaleString() : '—' },
                  { label: 'Durée totale', value: formatDuration(data.duration_seconds) },
                  { label: 'Statut', value: <StatusBadge status={data.status} /> },
                ].map(({ label, value, mono }) => (
                  <div key={label}>
                    <div className="text-xs text-gray-500 mb-1">{label}</div>
                    {typeof value === 'string'
                      ? <div className={`text-sm text-white font-medium ${mono ? 'font-mono' : ''}`}>{value}</div>
                      : value
                    }
                  </div>
                ))}
              </div>
            </div>

            {/* Top critical findings preview */}
            {sortedFindings.filter(f => ['critical', 'high'].includes((f.severity || '').toLowerCase())).length > 0 && (
              <div className="bg-brand-black-light rounded-xl border border-red-500/20 p-5 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-semibold text-red-400">Findings critiques / élevés</span>
                  </div>
                  <button onClick={() => setActiveTab('findings')} className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
                    Voir tous <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="space-y-2">
                  {sortedFindings.filter(f => ['critical', 'high'].includes((f.severity || '').toLowerCase())).slice(0, 5).map((f, i) => {
                    const sc = sevColor((f.severity || '').toLowerCase());
                    return (
                      <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${sc.bg} ${sc.border}`}>
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sc.dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-white">{f.title || f.finding || 'Finding'}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full border font-bold uppercase ${sc.badge}`}>{f.severity}</span>
                          </div>
                          {f.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{f.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* AI teaser */}
            <div
              onClick={() => setActiveTab('ai')}
              className="bg-gradient-to-r from-purple-500/5 to-brand-green/5 rounded-xl border border-purple-500/20 p-5 lg:col-span-2 cursor-pointer hover:border-purple-500/40 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">Analyse IA — Guide de compromission AD</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      L'IA explique les découvertes, les chemins d'attaque, et fournit un guide étape par étape pour compromettre l'AD.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-purple-400 group-hover:gap-3 transition-all">
                  <span className="text-sm font-medium">Générer</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Steps ── */}
        {activeTab === 'steps' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { id: 'all', label: `Toutes (${steps.length})` },
                { id: 'completed', label: `Réussies (${steps.filter(s => s.status === 'completed').length})` },
                { id: 'failed', label: `Échouées (${steps.filter(s => s.status === 'failed').length})` },
                { id: 'skipped', label: `Ignorées (${steps.filter(s => s.status === 'skipped').length})` },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setStepFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    stepFilter === f.id ? 'bg-brand-green/20 text-brand-green border border-brand-green/30' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {filteredSteps.map((step, i) => (
                <StepCard key={step.id || i} step={step} index={i} />
              ))}
              {filteredSteps.length === 0 && (
                <div className="text-center py-12 text-gray-500 text-sm">
                  <Terminal className="w-8 h-8 mx-auto mb-2 text-gray-700" />
                  Aucune étape correspondant au filtre
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Tab: Findings ── */}
        {activeTab === 'findings' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { id: 'all', label: `Tous (${sortedFindings.length})` },
                { id: 'critical', label: `Critique (${sevCounts.critical})`, color: 'text-red-400 border-red-500/30 bg-red-500/10' },
                { id: 'high', label: `Élevé (${sevCounts.high})`, color: 'text-orange-400 border-orange-500/30 bg-orange-500/10' },
                { id: 'medium', label: `Moyen (${sevCounts.medium})`, color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
                { id: 'low', label: `Faible (${sevCounts.low})`, color: 'text-blue-400 border-blue-500/30 bg-blue-500/10' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFindingFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    findingFilter === f.id
                      ? (f.color || 'bg-brand-green/20 text-brand-green border-brand-green/30')
                      : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {filteredFindings.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-500/40 mx-auto mb-3" />
                <p className="text-gray-400 font-medium">Aucun finding détecté</p>
                <p className="text-gray-600 text-sm mt-1">
                  {findingFilter !== 'all' ? 'Aucun finding avec ce niveau de sévérité.' : 'Le domaine semble sain ou les tests n\'ont pas abouti.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredFindings.map((finding, i) => (
                  <FindingCard key={i} finding={finding} index={i} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Timeline ── */}
        {activeTab === 'timeline' && (
          <div className="bg-brand-black-light rounded-xl border border-gray-800 p-5">
            {timeline.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucun événement de timeline</p>
              </div>
            ) : (
              <div className="space-y-1">
                {timeline.map((event, i) => {
                  const dotColor =
                    event.event_type === 'step_completed'      ? 'bg-green-400' :
                    event.event_type === 'step_failed'         ? 'bg-red-400' :
                    event.event_type === 'finding_discovered'  ? 'bg-orange-400' :
                    event.event_type === 'credential_found'    ? 'bg-yellow-400' :
                    'bg-blue-400';
                  return (
                    <div key={i} className="flex gap-4">
                      <div className="flex flex-col items-center w-6 flex-shrink-0">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${dotColor} ring-2 ring-brand-black`} />
                        {i < timeline.length - 1 && <div className="w-px flex-1 bg-gray-800 mt-1" />}
                      </div>
                      <div className="flex-1 pb-4 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-medium text-white">{event.step_name || event.event || event.event_type}</span>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{event.description}</p>
                        )}
                        {event.ai_explanation && (
                          <p className="text-xs text-brand-green/70 mt-1 italic">{event.ai_explanation}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: AI Analysis ── */}
        {activeTab === 'ai' && (
          <div className="space-y-6">
            {/* Sub-tabs */}
            {aiAnalysis && (
              <div className="flex gap-2 border-b border-gray-800 pb-0">
                {[
                  { id: 'analysis', label: 'Analyse & Guide d\'attaque', icon: <Brain className="w-3.5 h-3.5" /> },
                  { id: 'evidence', label: `Preuves & Captures (${sortedFindings.filter(f => f.evidence).length})`, icon: <Camera className="w-3.5 h-3.5" /> },
                ].map(st => (
                  <button
                    key={st.id}
                    onClick={() => setAiSubTab(st.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-colors ${
                      aiSubTab === st.id ? 'border-purple-500 text-purple-400' : 'border-transparent text-gray-400 hover:text-white'
                    }`}
                  >
                    {st.icon} {st.label}
                  </button>
                ))}
              </div>
            )}

            {/* Generate button / loading */}
            {!aiAnalysis && !loadingAI && (
              <div className="bg-brand-black-light rounded-xl border border-purple-500/20 p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Analyse IA — Guide de compromission AD</h3>
                <p className="text-gray-400 text-sm mb-2 max-w-xl mx-auto">
                  L'IA va analyser tous les findings et étapes de cette session pour :
                </p>
                <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto mb-6 text-left">
                  {[
                    { icon: <Lightbulb className="w-4 h-4 text-yellow-400" />, text: 'Expliquer ce qui a été découvert' },
                    { icon: <Crosshair className="w-4 h-4 text-orange-400" />, text: 'Identifier les chemins d\'attaque' },
                    { icon: <Sword className="w-4 h-4 text-red-400" />, text: 'Guide étape par étape pour compromettre l\'AD' },
                    { icon: <ShieldAlert className="w-4 h-4 text-brand-green" />, text: 'Recommandations de remédiation prioritaires' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 bg-gray-900 rounded-lg border border-gray-800">
                      {item.icon}
                      <span className="text-xs text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
                {aiError && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                    {aiError}
                  </div>
                )}
                <button
                  onClick={generateAIAnalysis}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/30 border border-purple-500/30 transition-all font-semibold mx-auto"
                >
                  <Sparkles className="w-4 h-4" /> Générer l'analyse IA
                </button>
                <p className="text-xs text-gray-600 mt-3">Génération via Ollama (qwen2.5-coder:32b) — environ 2-4 minutes</p>
              </div>
            )}

            {loadingAI && (
              <div className="bg-brand-black-light rounded-xl border border-purple-500/20 p-8 text-center">
                <div className="relative w-16 h-16 mx-auto mb-4">
                  <div className="absolute inset-0 rounded-full border-2 border-purple-500/30 animate-ping" />
                  <div className="absolute inset-0 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Brain className="w-7 h-7 text-purple-400 animate-pulse" />
                  </div>
                </div>
                <p className="text-white font-semibold mb-1">Analyse IA en cours…</p>
                <p className="text-gray-400 text-sm">Génération automatique du guide de compromission AD</p>
                <p className="text-gray-600 text-xs mt-2">Cela peut prendre 2 à 4 minutes — traitement en tâche de fond</p>
                <div className="mt-4 flex justify-center gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}

            {/* Analysis results */}
            {aiAnalysis && aiSubTab === 'analysis' && (
              <div className="space-y-4">
                {/* Header with meta */}
                <div className="flex items-center justify-between p-4 bg-purple-500/5 rounded-xl border border-purple-500/20">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <div>
                      <span className="text-sm font-semibold text-white">Analyse générée pour {aiAnalysis.domain}</span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {aiAnalysis.steps_analyzed} étapes · {aiAnalysis.findings_count} findings analysés
                        {aiAnalysis.credentials_found > 0 && ` · ${aiAnalysis.credentials_found} credentials`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={generateAIAnalysis}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-xs hover:bg-purple-500/20 border border-purple-500/30 transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" /> Regénérer
                  </button>
                </div>

                {/* Section blocks */}
                {sectionDefs.map(def => (
                  <AISectionBlock
                    key={def.key}
                    title={def.label}
                    icon={def.icon}
                    content={aiSections[def.key] || ''}
                    color={def.color}
                    collapsible={true}
                  />
                ))}

                {/* Raw section if no parsed sections */}
                {Object.keys(aiSections).length === 0 && aiAnalysis.raw && (
                  <div className="bg-brand-black-light rounded-xl border border-gray-700 p-5">
                    <div className="text-sm font-semibold text-gray-400 mb-3">Réponse brute</div>
                    <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{aiAnalysis.raw}</div>
                  </div>
                )}

                {aiAnalysis.error && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
                    Note : L'analyse a utilisé un fallback (service IA indisponible). {aiAnalysis.error}
                  </div>
                )}
              </div>
            )}

            {/* Evidence captures */}
            {aiAnalysis && aiSubTab === 'evidence' && (
              <EvidenceCaptures findings={sortedFindings} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ADSessionDetail;
